import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Clock, MessageSquareText, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public, unauthenticated per-brand webchat landing page (/c/:firmId).
// A lead's very first touch — fully white-labeled — with the brand's live Penny widget embedded.
export default function BrandLanding() {
  const { firmId = "expat-tax-firm" } = useParams();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Book a Free Consult";
    axios.get(`${API}/widget/config?firm_id=${encodeURIComponent(firmId)}`)
      .then((r) => setCfg(r.data))
      .catch(() => setCfg(null))
      .finally(() => setLoading(false));
  }, [firmId]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950"><Loader2 className="h-7 w-7 animate-spin text-white/70" /></div>;
  }

  const color = cfg?.primary_color || "#1F2937";
  const accent = cfg?.accent_color || "#E8C84E";
  const brandName = cfg?.brand_name || "Expat Tax Firm";
  const logo = cfg?.logo_url;
  const specialty = cfg?.specialty || "expert U.S. tax filing";

  const bullets = [
    ["ShieldCheck", "Free, no-pressure consultation with a specialist"],
    ["Clock", "Pick a time that works for you — done in minutes"],
    ["MessageSquareText", "Chat with Penny now — ask anything, no commitment"],
  ];
  const ICONS = { ShieldCheck, Clock, MessageSquareText };

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(160deg, ${color} 0%, ${color} 46%, #0b1220 100%)` }} data-testid="brand-landing">
      {/* Top bar */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center">
        {logo ? (
          <span className="bg-white rounded-xl px-3 py-2 shadow-lg inline-flex items-center" data-testid="landing-logo">
            <img src={logo} alt={brandName} className="h-8 w-auto object-contain" />
          </span>
        ) : (
          <span className="text-white font-heading text-xl font-bold">{brandName}</span>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 pb-16 grid lg:grid-cols-2 gap-10 items-start">
        {/* Left: branded pitch */}
        <div className="pt-6 lg:pt-12 text-white">
          <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold mb-5"
            style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}55` }} data-testid="landing-eyebrow">
            {brandName}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight tracking-tight">
            Book your <span style={{ color: accent }}>free consult</span> with a tax specialist.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/70 max-w-lg leading-relaxed">
            {brandName} specializes in {specialty}. Chat with Penny to ask a question, get a quote, or schedule a free
            consultation — right here, right now.
          </p>
          <div className="mt-8 space-y-3.5 max-w-md">
            {bullets.map(([icon, text]) => {
              const Ic = ICONS[icon];
              return (
                <div key={text} className="flex items-center gap-3 text-sm sm:text-base text-white/85">
                  <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}22` }}>
                    <Ic className="h-4 w-4" style={{ color: accent }} />
                  </span>
                  {text}
                </div>
              );
            })}
          </div>
          <a href="#book" className="mt-9 inline-flex items-center gap-2 rounded-xl px-6 py-3 font-semibold shadow-lg transition-transform hover:scale-[1.02]"
            style={{ background: accent, color: color }} data-testid="landing-cta">
            Chat with Penny →
          </a>
          <p className="mt-10 text-xs text-white/40">Powered by Tax Flow · Your information is private &amp; secure.</p>
        </div>

        {/* Right: live, brand-configured Penny widget (embedded, open) */}
        <div id="book" className="lg:pt-8 flex justify-center lg:justify-end">
          <div className="relative w-[min(420px,100%)]">
            <div className="absolute -inset-3 rounded-3xl opacity-40 blur-2xl" style={{ background: accent }} />
            <div className="relative rounded-3xl overflow-hidden bg-white shadow-2xl ring-1 ring-black/10" style={{ height: 660 }}>
              <iframe
                title={`Chat with Penny — ${brandName}`}
                src={`/widget?firm=${encodeURIComponent(firmId)}&open=1`}
                className="w-full h-full border-0"
                allow="clipboard-write"
                data-testid="landing-widget-frame"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
